import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { setAdminSession } from '../../utils/adminSession';
import { validateUrl } from '../../utils/apiEndpoints';

const PageContainer = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const RequiredMark = styled.span`
  color: #f97373;
  margin-left: 0.15rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const Login: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!apiKey) {
      setErrorMessage('Please enter an API key.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(validateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ apiKey }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        const message = data?.message || 'Invalid API key';
        throw new Error(message);
      }

      const name = data?.name || 'Admin';

      setAdminSession(apiKey, name);
      sessionStorage.setItem('admin_welcome_name', name);

      navigate('/admin', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed.';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Title>Admin Login</Title>
      <Form onSubmit={handleLogin}>
        <FieldGroup>
          <Label htmlFor="apiKey">
            API Key<RequiredMark>*</RequiredMark>
          </Label>
          <Input
            id="apiKey"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            required
          />
        </FieldGroup>

        <SubmitButton type="submit" disabled={submitting}>
          {submitting ? 'Validating…' : 'Login'}
        </SubmitButton>
      </Form>

      {errorMessage ? (
        <ErrorMessage>
          {errorMessage}
          <ActionsRow>
            <SecondaryButton type="button" onClick={() => setErrorMessage(null)}>
              Retry
            </SecondaryButton>
            <LinkButton to="/">Return Home</LinkButton>
          </ActionsRow>
        </ErrorMessage>
      ) : null}
    </PageContainer>
  );
};

export default Login;

